// @ts-strict-ignore
import Alert from 'antd/lib/alert';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import { WarningFilled } from '@ant-design/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tryUpdateRelease, UpdatePhasedReleaseRequestBaseReleaseStep } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import {
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    isPhasedReleasePausedByRunway,
    isPhasedReleaseSuperseded,
} from '../helpers';
import { RootState } from '../reducers/reducers';
import Checkbox from 'antd/lib/checkbox';
import { globalMessage } from '../utils/globalMessage';
import {
    AppleAppStorePhasedReleaseState,
    AppPlatform,
    AppStoreType,
    Automation,
    AutomationType,
    GooglePlayStoreState,
    HuaweiPhasedReleaseState,
    PhasedRelease,
    RolloutAction,
    RunwayResponseType,
} from '../types';
import { convertAndroidStagedRolloutPercentage } from '../helpers/displayHelpers';
import { stagedRolloutPercentageInputValidationRules } from '@/helpers/formHelpers';
import { AndroidStagedRolloutPercentageInput } from './sharedComponents/shared';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { selectAutomationsByType } from '@/selectors/selectors';
import { StepRunUpdatable, UpdatePhasedReleaseRequest } from '@/actions/stepRun';
import _ from 'lodash';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface UpdateStagedRolloutModalProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    open: boolean;
    isPaused: boolean;
    canEditUserFraction: boolean;
    userFraction?: number;
    onSuccess: () => void;
    onClose: () => void;
    phasedRelease: Nullable<PhasedRelease>;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const UpdateStagedRolloutModal: React.FC<UpdateStagedRolloutModalProps> = (props: UpdateStagedRolloutModalProps) => {
    const [form] = useForm();
    const dispatch = useDispatch();
    const isGooglePlayStore = getIsGooglePlayStore(props.appStoreType);
    const isAppleAppStore = getIsAppleAppStore(props.appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(props.appStoreType);

    const requestTypeForReleaseStep =
        isGooglePlayStore ? ReleaseUpdatable.UpdateGooglePhasedRelease
        : isAppleAppStore ? ReleaseUpdatable.ResumeApplePhasedRelease
        : isHuaweiAppGallery ? ReleaseUpdatable.UpdateHuaweiPhasedRelease
        : null;

    const requestType =
        isGooglePlayStore ? StepRunUpdatable.UpdateGooglePhasedRelease
        : isAppleAppStore ? StepRunUpdatable.ResumeApplePhasedRelease
        : isHuaweiAppGallery ? StepRunUpdatable.UpdateHuaweiPhasedRelease
        : null;

    const rolloutAutomations: Automation[] = useSelector((state: RootState) => {
        return _.compact([
            selectAutomationsByType(state, props.appId, AutomationType.AutoHaltUnstablePhasedReleases)?.[0],
            selectAutomationsByType(state, props.appId, AutomationType.AutoPromoteStablePhasedReleases)?.[0],
        ]);
    });

    const [
        updateStagedReleaseStepRun,
        updateStagedReleaseIsLoadingStepRun,
        updateStagedReleaseResponseTypeStepRun,
        updateStagedReleaseErrorStepRun,
    ] = useCallUpdatable(requestType, props.stepRunComponentProps);

    const updateStagedReleaseIsLoadingReleaseStep = useIsLoading(requestTypeForReleaseStep, props.releaseId);
    const updateStagedReleaseErrorReleaseStep = useError(requestTypeForReleaseStep, props.releaseId);
    const updateStagedReleaseSuccessReleaseStep = useUpdated(requestTypeForReleaseStep, props.releaseId);

    const updateStagedReleaseIsLoading =
        props.stepRunComponentProps ? updateStagedReleaseIsLoadingStepRun : updateStagedReleaseIsLoadingReleaseStep;
    const updateStagedReleaseError =
        props.stepRunComponentProps ? updateStagedReleaseErrorStepRun : updateStagedReleaseErrorReleaseStep;
    const updateStagedReleaseSuccess =
        props.stepRunComponentProps ?
            updateStagedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   updateStagedReleaseSuccessReleaseStep;

    const isRunwayPausedRelease = isPhasedReleasePausedByRunway(props.appStoreType, props.phasedRelease);
    // A superseded rollout is rejected by the app store, so the form can only fail from here.
    const isSuperseded = isPhasedReleaseSuperseded(props.phasedRelease ?? undefined);
    const hasRolloutAutomations = rolloutAutomations.length > 0;
    const hasAutoHaltAutomation = rolloutAutomations.some(
        (automation) => automation.type === AutomationType.AutoHaltUnstablePhasedReleases,
    );

    const [hasSelectedAutomations, setHasSelectedAutomations] = React.useState(props.isPaused && hasRolloutAutomations);
    const [autoHaltSelected, setAutoHaltSelected] = React.useState(props.isPaused && hasAutoHaltAutomation);

    const updateStagedRollout = () => {
        form.validateFields().then((values) => {
            const selectedAutomations = rolloutAutomations
                ?.filter((automation) => values[automation.type])
                .map((automation) => automation.type);

            const genericRequest: UpdatePhasedReleaseRequestBaseReleaseStep = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                actionType: props.isPaused ? RolloutAction.Resume : RolloutAction.UpdatePercentage,
                resumeAutomations: selectedAutomations,
            };

            let request: UpdatePhasedReleaseRequest;
            if (isGooglePlayStore) {
                const userFractionPercent = values.userFraction;
                const userFraction = userFractionPercent / 100;
                let phaseState = GooglePlayStoreState.InProgress;
                if (userFraction === 1) {
                    phaseState = GooglePlayStoreState.Completed;
                } else if (userFraction === 0) {
                    phaseState = GooglePlayStoreState.Halted;
                }
                request = {
                    userFraction,
                    phaseState,
                    ...genericRequest,
                };
            } else if (isAppleAppStore) {
                request = {
                    userFraction: null,
                    phaseState: AppleAppStorePhasedReleaseState.Active,
                    ...genericRequest,
                };
            } else if (isHuaweiAppGallery) {
                const userFractionPercent = values.userFraction;
                const userFraction = userFractionPercent / 100;
                let phaseState = HuaweiPhasedReleaseState.Released;
                if (userFraction === 0) {
                    phaseState = HuaweiPhasedReleaseState.Suspended;
                }
                request = {
                    userFraction,
                    phaseState,
                    ...genericRequest,
                };
            }

            if (request) {
                if (props.stepRunComponentProps) {
                    updateStagedReleaseStepRun(request);
                } else {
                    const releaseStepRequest: UpdatePhasedReleaseRequestBaseReleaseStep = {
                        ...genericRequest,
                        ...request,
                    };
                    dispatch(tryUpdateRelease(requestTypeForReleaseStep, releaseStepRequest, props.releaseId));
                }
            }
        });
    };

    React.useEffect(() => {
        if (updateStagedReleaseError) {
            globalMessage.error(updateStagedReleaseError);
        }
    }, [updateStagedReleaseError]);

    React.useEffect(() => {
        if (updateStagedReleaseSuccess) {
            globalMessage.success(isGooglePlayStore ? 'Staged rollout updated!' : 'Phased rollout updated!');

            props.onSuccess();
        }
    }, [updateStagedReleaseSuccess]);

    const onValuesChange = () => {
        const selectedAutomations = rolloutAutomations
            ?.map((automation) => automation.type)
            .filter((id) => form.getFieldValue(id));

        setAutoHaltSelected(selectedAutomations.includes(AutomationType.AutoHaltUnstablePhasedReleases));

        const hasNowSelectedAutomations = selectedAutomations.length > 0;
        if (hasNowSelectedAutomations != hasSelectedAutomations) {
            setHasSelectedAutomations(hasNowSelectedAutomations);
        }
    };

    let submitButtonTitle: string;
    if (props.isPaused && hasSelectedAutomations) {
        submitButtonTitle = 'Resume rollout with selected automations';
    } else if (props.isPaused) {
        submitButtonTitle = 'Resume rollout with no selected automations';
    } else {
        // Only Android uses this component if the rollout isn't paused.
        submitButtonTitle = 'Edit staged rollout percentage';
    }

    const headerSpacing = 24;
    return (
        <Modal
            title={props.isPaused ? 'Resume rollout' : 'Edit staged rollout percentage'}
            open={props.open}
            okText={submitButtonTitle}
            onOk={() => form.submit()}
            onCancel={props.onClose}
            okButtonProps={{ loading: updateStagedReleaseIsLoading, disabled: isSuperseded }}
            destroyOnClose={true}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {isSuperseded && (
                    <Alert
                        showIcon
                        icon={<WarningFilled style={{ marginTop: 4 }} />}
                        closable={false}
                        message="This release has been superseded by a newer version, so its rollout can no longer be updated."
                        type="warning"
                        style={{ alignItems: 'flex-start', marginBottom: headerSpacing }}
                    />
                )}
                {props.isPaused && (
                    <Text type="secondary" style={{ marginBottom: headerSpacing }}>
                        {isGooglePlayStore || isHuaweiAppGallery ?
                            'If you are resuming a rollout and there is not another release already in progress, Runway will resume to the specified percentage. If there is another release already in progress, Runway will release this update to all users immediately.'
                        :   'Runway will resume your phased rollout.'}
                    </Text>
                )}
                <Form
                    form={form}
                    preserve={false}
                    onValuesChange={onValuesChange}
                    onFinish={() => updateStagedRollout()}
                    layout="vertical"
                >
                    {(isGooglePlayStore || isHuaweiAppGallery) && (
                        <FormItem
                            label={
                                <>
                                    Rollout percentage
                                    <TooltipInfoIcon
                                        tooltipContent="Specify the percentage of your user base to which you want to roll out this version."
                                        style={{ marginLeft: 6 }}
                                    />
                                </>
                            }
                            required
                        >
                            <FormItem
                                name="userFraction"
                                noStyle
                                initialValue={convertAndroidStagedRolloutPercentage(props.userFraction)}
                                rules={[
                                    {
                                        required: true,
                                    },
                                    ...stagedRolloutPercentageInputValidationRules,
                                ]}
                                label="Rollout percentage"
                            >
                                <AndroidStagedRolloutPercentageInput disabled={!props.canEditUserFraction} />
                            </FormItem>
                        </FormItem>
                    )}
                    {props.isPaused && hasRolloutAutomations && (
                        <>
                            <Text strong>Resume the selected automations</Text>
                            <FormItem style={{ marginTop: 12 }}>
                                {rolloutAutomations?.map((automation) => {
                                    const description = `'${automation.name}' automation`;
                                    return (
                                        <FormItem
                                            initialValue={true}
                                            key={automation.type}
                                            name={automation.type}
                                            valuePropName="checked"
                                            style={{ marginBottom: '0px' }}
                                        >
                                            <Checkbox disabled={!props.canEditUserFraction}>{description}</Checkbox>
                                        </FormItem>
                                    );
                                })}
                            </FormItem>
                        </>
                    )}
                    {isRunwayPausedRelease && autoHaltSelected && (
                        <Alert
                            showIcon
                            icon={<WarningFilled style={{ marginTop: 4 }} />}
                            closable={false}
                            message="An automation halted your rollout. Unpausing the automation may cause your rollout to be halted again, unless the related health metrics have stabilized or health thresholds were modified from App settings."
                            type="warning"
                            style={{ alignItems: 'flex-start' }}
                        />
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default UpdateStagedRolloutModal;
